/**
 * The recitation engine's browser half — §5.4(b) the reconciler and §5.4(c) the
 * observer.
 *
 * ROOT CAUSE OF THE HISTORICAL BREAKAGE: six independent effects each wrote to the
 * audio element (`src`, `currentTime`, `play()`, `pause()`, `playbackRate`), each
 * with its own dependency array. Any two of them firing in an unexpected order
 * produced a bug that looked like a logic error but was an ordering error.
 *
 * So: THIS FILE IS THE ONLY CODE IN THE APP THAT TOUCHES THE AUDIO ELEMENT (A2).
 * Everything else goes through the reducer.
 */
import { useEffect, useRef } from 'react'
import { useRouter } from '@tanstack/react-router'
import {
  TOTAL_PAGES,
  applyTick,
  ayahKeysOnPage,
  ayahStartMs,
  pageOfVerse,
  parseVerseKey,
  planResync,
  planTick,
  type Meta,
  type ReciterAudio,
  type TickInput,
} from '@mushaf/corpus'
import { useAudioStatus } from '../stores/audioStatus'
import { usePlayer } from '../stores/player'
import { selectAudioEpoch, useSettings } from '../stores/settings'
import { useMeta, useReciterAudio } from '../lib/queries'
import { useQueueGrowth } from './useQueueGrowth'
import { usePageFollow } from './usePageFollow'

/**
 * A1/L11 — the <audio> element is mounted ABOVE THE ROUTER and NEVER UNMOUNTED.
 * Turning a page must not stop playback.
 */
export function AudioEngine() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const { data: meta } = useMeta()
  const reciterId = useSettings((s) => s.reciterId)
  const { data: manifest } = useReciterAudio(reciterId)

  return (
    <>
      {/* Not `hidden`: a hidden media element is still a media element, but keeping
          it in the tree with no controls is the simplest thing that cannot be
          remounted by a route change. */}
      <audio ref={audioRef} preload="metadata" data-testid="mushaf-audio" className="sr-only" />
      <Reconciler audioRef={audioRef} manifest={manifest} />
      <Observer audioRef={audioRef} manifest={manifest} meta={meta} />
      <QueueGrowth meta={meta} />
      <PageFollow meta={meta} />
    </>
  )
}

type AudioRef = React.RefObject<HTMLAudioElement | null>

// ---------------------------------------------------------------------------
// (b) The reconciler — ONE effect, the only writer
// ---------------------------------------------------------------------------

function Reconciler({ audioRef, manifest }: { audioRef: AudioRef; manifest?: ReciterAudio }) {
  const currentKey = usePlayer((s) => s.currentKey)
  const isPlaying = usePlayer((s) => s.isPlaying)
  const pendingSeekMs = usePlayer((s) => s.pendingSeekMs)
  const armSeek = usePlayer((s) => s.armSeek)
  const consumeSeek = usePlayer((s) => s.consumeSeek)
  const playbackRate = useSettings((s) => s.playbackRate)
  /**
   * THE RESTART SIGNAL. Bumped by every audio/hifz settings edit (`stores/settings.ts`),
   * and by nothing else — in particular NOT by the observer's tick, which is what keeps
   * L10/A6 intact: advancing inside a passage still does nothing at all.
   *
   * It is an epoch read by the one existing effect rather than a `store.subscribe` that
   * seeks directly, because A2 says this file has exactly ONE writer of the element. A
   * subscription would be a second one, with its own ordering relative to this effect —
   * which is precisely the six-effects failure the architecture exists to prevent.
   */
  const audioEpoch = useSettings(selectAudioEpoch)

  /**
   * Read the play-intent through a REF here, not as an effect dependency. Re-running
   * the reconciler on *pause* would rewind, because a run that has just finished sits
   * a few ms PAST its last ayah and therefore reads as "not inside" (§5.4b case 3).
   */
  const playingRef = useRef(isPlaying)
  const seekRef = useRef<number | null>(null)
  /**
   * Seeded with the CURRENT epoch, not 0: mounting is not a settings edit, and a mount
   * that read as one would re-seek on top of the src-load path below.
   */
  const appliedEpoch = useRef(audioEpoch)

  // Synced in an effect rather than during render: writing a ref mid-render is not
  // safe under concurrent rendering, and the consumers (the loadedmetadata handler
  // and the rAF loop) only ever read it after commit anyway.
  useEffect(() => {
    playingRef.current = isPlaying
  }, [isPlaying])

  useEffect(() => {
    seekRef.current = pendingSeekMs
  }, [pendingSeekMs])

  // --- the one-shot seek, fired from loadedmetadata (L8) -------------------
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onLoadedMetadata = () => {
      // L8: `currentTime` is only settable after `loadedmetadata`. Assigning earlier
      // is silently discarded and playback lands at 0:00 of a two-hour surah.
      const ms = seekRef.current
      if (ms !== null) {
        audio.currentTime = ms / 1000
        consumeSeek()
      }
      // L13: LOADING A RESOURCE RESETS playbackRate TO defaultPlaybackRate. Re-apply
      // it here, or a reciter switch silently drops back to 1×.
      const rate = useSettings.getState().playbackRate
      audio.defaultPlaybackRate = rate
      audio.playbackRate = rate

      if (playingRef.current) void audio.play().catch(() => {})
    }

    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    return () => audio.removeEventListener('loadedmetadata', onLoadedMetadata)
  }, [audioRef, consumeSeek])

  // --- the reconciliation itself ------------------------------------------
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !manifest) return

    /**
     * Consumed AFTER the manifest guard and BEFORE the queue guards, and the placement
     * is deliberate in both directions.
     *
     * After the manifest guard: a reciter switch makes `manifest` undefined while the
     * new one is fetched (the query has no `placeholderData`), so consuming the epoch
     * any earlier would burn the restart on a run that cannot act on it — and the user
     * would hear the old voice carry on.
     *
     * Before the queue guards: a settings edit made with nothing queued has no ayah to
     * restart, and leaving the flag armed would fire a stale seek at the start of the
     * next unrelated recitation.
     */
    const settingsEdited = appliedEpoch.current !== audioEpoch
    appliedEpoch.current = audioEpoch

    const here = currentKey ? parseVerseKey(currentKey) : null
    if (!here) {
      if (!audio.paused) audio.pause()
      return
    }

    const surah = manifest.surahs[String(here.surah)]
    if (!surah) return

    const desiredSrc = surah.url
    const startMs = ayahStartMs(surah, here.ayah)

    /**
     * A3 — KEY THE "SAME FILE?" CHECK ON THE URL, NEVER THE SURAH NUMBER.
     *
     * Every reciter has their own file per surah, so keying on surah makes a reciter
     * switch look like "same file, different timings": the engine takes the seek-only
     * path and moves the old voice to the new reciter's offset.
     * Symptom: "it restarted the ayah and ignored me."
     */
    const currentSrc = audio.currentSrc || audio.src
    if (currentSrc !== desiredSrc) {
      audio.src = desiredSrc
      audio.load()
      armSeek(startMs)
      return // the seek fires from loadedmetadata (A4)
    }

    if (startMs !== null) {
      const endMs = surah.t[here.ayah] ?? Infinity
      const ms = audio.currentTime * 1000
      const inside = ms >= startMs && ms < endMs

      /**
       * `settingsEdited` is the ONLY thing that overrides "already inside → do nothing".
       * A settings edit restarts the sounding ayah from its own beginning under the new
       * settings; every other caller of this effect keeps A6's gapless behaviour.
       */
      if (!inside || settingsEdited) {
        audio.currentTime = startMs / 1000
        /**
         * The element may have run off the end of the file and paused itself, and
         * seeking back into it does NOT resume. So play explicitly — but only when
         * the intent (read through the ref) is to be playing.
         */
        if (playingRef.current && audio.paused) void audio.play().catch(() => {})
      }
      // A6/L10: same src, already inside the target ayah → DO NOTHING AT ALL. This is
      // what keeps a passage gapless.
    }

    if (isPlaying && audio.paused) void audio.play().catch(() => {})
    if (!isPlaying && !audio.paused) audio.pause()
  }, [audioRef, manifest, currentKey, isPlaying, armSeek, audioEpoch])

  // --- rate ----------------------------------------------------------------
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    // Set BOTH, or the next load resets to the default (L13).
    audio.defaultPlaybackRate = playbackRate
    audio.playbackRate = playbackRate
  }, [audioRef, playbackRate])

  /**
   * --- the buffering observation -------------------------------------------
   *
   * READ-ONLY: this effect never writes to the element, so it does not break A2. It
   * exists because a reciter-page jump crosses into ANOTHER SURAH FILE, and the src
   * assignment above is followed by a network fetch of a whole surah — seconds on a
   * slow link, during which the transport looked exactly like a broken button.
   *
   * DERIVED FROM readyState, NOT FROM PAIRED EVENTS. A `loadstart`-sets /
   * `canplay`-clears pair strands the flag on forever: with `preload="metadata"` a
   * paused element settles at HAVE_METADATA and `canplay` never comes. So every
   * event merely re-reads the truth off the element.
   *
   * It is also gated on the PLAY INTENT. Buffering while paused is not something the
   * user is waiting for, and T28's jump-while-paused must stay instant.
   */
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const setBuffering = useAudioStatus.getState().setBuffering
    const sync = () => {
      const wanted = usePlayer.getState().isPlaying
      // HAVE_FUTURE_DATA — the element can play the current position onwards. Anything
      // below it means the next sound is still on the wire.
      setBuffering(wanted && !audio.error && audio.readyState < 3)
    }

    const EVENTS = [
      'emptied',
      'loadstart',
      'loadedmetadata',
      'loadeddata',
      'canplay',
      'canplaythrough',
      'playing',
      'play',
      'pause',
      'waiting',
      'stalled',
      'seeking',
      'seeked',
      'progress',
      'suspend',
      'ended',
      'error',
      'abort',
    ] as const

    for (const e of EVENTS) audio.addEventListener(e, sync)
    // The element is mid-load whenever `currentKey`/`isPlaying` change, and those two
    // are dependencies, so this covers the intent flipping with no event of its own.
    sync()

    return () => {
      for (const e of EVENTS) audio.removeEventListener(e, sync)
    }
    // Declared AFTER the reconciliation effect so that on a src change it reads the
    // element the reconciler has already emptied, rather than the previous file.
  }, [audioRef, currentKey, isPlaying])

  return null
}

// ---------------------------------------------------------------------------
// (c) The observer — ONE tick body, started once, with two clocks
//
// STILL ONE OBSERVER. The tick body is the single place a decision is taken; what
// changed is that rAF is no longer its only driver, because rAF stops in a hidden tab
// and the audio does not (L26). Exactly one driver is live at a time, chosen by
// visibility, so the "six effects racing each other" failure cannot come back.
// ---------------------------------------------------------------------------

function Observer({
  audioRef,
  manifest,
  meta,
}: {
  audioRef: AudioRef
  manifest?: ReciterAudio
  meta?: Meta
}) {
  /**
   * A9 — the loop is STARTED ONCE and reads fresh state THROUGH A MUTABLE REF rather
   * than through dependencies. Restarting it on every tick would reset the frame
   * cadence constantly and miss boundaries.
   */
  const latest = useRef({ manifest, meta })

  useEffect(() => {
    latest.current = { manifest, meta }
  }, [manifest, meta])

  useEffect(() => {
    let frame = 0

    /**
     * Everything a decision needs, read fresh off the element and the stores at the
     * moment of asking. `null` means there is nothing to decide about.
     */
    const read = () => {
      const audio = audioRef.current
      const { manifest: m, meta: index } = latest.current
      if (!audio || !m) return null

      const state = usePlayer.getState()
      if (!state.isPlaying || !state.currentKey) return null

      const here = parseVerseKey(state.currentKey)
      if (!here) return null
      const surah = m.surahs[String(here.surah)]
      if (!surah) return null

      const input: TickInput = {
        state,
        audio: surah,
        ms: audio.currentTime * 1000,
        // L12: `ended` is the AUTHORITY for "this file is finished"; the timestamp is
        // only a hint. A surah's final timestamp routinely sits past the end of its
        // own file.
        ended: audio.ended,
        autoAdvancePage: useSettings.getState().autoAdvancePage,
        recitedPage: index ? pageOfVerse(index.pages, state.currentKey) : null,
      }
      return { audio, index, state, input }
    }

    const tick = () => {
      const ctx = read()
      if (!ctx) return
      const { audio, state, input } = ctx

      const decision = planTick(input)

      switch (decision.type) {
        case 'none':
        case 'await-growth':
          // A10: return WITHOUT pausing. The end of the queue is not the end of the
          // recitation while the next page is still on its way.
          return

        case 'ayah-repeat': {
          // The ONLY mid-run seek.
          audio.currentTime = decision.seekMs / 1000
          // Seeking back out of `ended` leaves the element paused.
          if (audio.paused) void audio.play().catch(() => {})
          usePlayer.setState(applyTick(state, decision))
          return
        }

        case 'stop': {
          audio.pause()
          usePlayer.setState(applyTick(state, decision))
          return
        }

        default:
          // follow-stream / next-in-queue / passage-repeat are pure index moves; the
          // reconciler decides whether any element work follows.
          usePlayer.setState(applyTick(state, decision))
      }
    }

    /**
     * L25 — adopt the ayah actually sounding, for the one case the tick cannot reach it.
     * Fires on return from a hidden tab: if the stream ran clean out of the queue while
     * nothing was driving the decision table, stepping towards it is hopeless, so the
     * queue is rebuilt around it (§5.6 `resyncTo`).
     */
    const recover = () => {
      const ctx = read()
      if (!ctx?.index) return

      const decision = planResync(ctx.input)
      if (decision.type !== 'resync') return

      const page = pageOfVerse(ctx.index.pages, decision.key)
      if (page === null) return
      const keys = ayahKeysOnPage(ctx.index, page)
      // The store action, not setState: recovery is a player mutation like any other.
      if (keys.length > 0) usePlayer.getState().resyncTo(keys, decision.key)
    }

    /**
     * A9 — the loop is started ONCE. Scheduling is separated from the tick body here
     * because the body now has other callers, and a body that re-arms rAF would start a
     * SECOND self-sustaining loop on every event that called it.
     */
    const loop = () => {
      frame = requestAnimationFrame(loop)
      // Hidden: the media events below are the driver. Never both at once — two drivers
      // reaching case 1 in the same instant would consume two repeats for one.
      if (document.visibilityState === 'hidden') return
      tick()
    }

    /**
     * L26 — A HIDDEN TAB NEEDS A DRIVER THAT IS NOT A FRAME.
     *
     * rAF is paused in a background tab; media playback is not. With rAF as the only
     * driver, backgrounding the reader left it streaming on with its decision table
     * switched off: an infinite ayah repeat was simply not enforced, a finished drill
     * never stopped, the queue never grew and the page never followed — and the first
     * frame after coming back seeked minutes backwards to the ayah still marked for
     * repeat. Symptom: "it kept reciting other pages, then jumped back when I returned."
     *
     * So while hidden, the ELEMENT drives: `timeupdate` is playback-driven rather than
     * frame-driven, and a background timer would be clamped slower than its ~4 Hz.
     * L9 still holds where it was measured — rAF remains the driver while visible,
     * because ~4 Hz overshoots a repeat by up to 250 ms and that is audible.
     */
    const onMediaProgress = () => {
      if (document.visibilityState !== 'hidden') return
      tick()
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') return
      // Adopt reality first, then act on it: recovery moves the index, and the tick that
      // follows decides what the element does about where the index landed.
      recover()
      tick()
    }

    const element = audioRef.current
    element?.addEventListener('timeupdate', onMediaProgress)
    // L12 again: `ended` is the only authority for the last ayah of a surah, and while
    // hidden it is the only event that will ever report it.
    element?.addEventListener('ended', onMediaProgress)
    document.addEventListener('visibilitychange', onVisibility)

    frame = requestAnimationFrame(loop)
    return () => {
      cancelAnimationFrame(frame)
      element?.removeEventListener('timeupdate', onMediaProgress)
      element?.removeEventListener('ended', onMediaProgress)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [audioRef])

  return null
}

// ---------------------------------------------------------------------------
// Queue growth and page following, as components so they can use hooks
// ---------------------------------------------------------------------------

function QueueGrowth({ meta }: { meta?: Meta }) {
  useQueueGrowth(meta)
  return null
}

function PageFollow({ meta }: { meta?: Meta }) {
  const router = useRouter()
  usePageFollow(meta, (page) => {
    if (page >= 1 && page <= TOTAL_PAGES)
      void router.navigate({ to: '/read/$page', params: { page: String(page) } })
  })
  return null
}
